import ProductionStorage from '../utils/productionStorage';

export interface UserRegistration {
  id: string;
  name: string;
  username: string;
  email: string;
  phone: string;
  userType: 'customer' | 'business';
  registrationDate: string;
  lastLogin?: string;
  isActive: boolean;
}

const REGISTRATIONS_STORAGE_KEY = 'bookapt_user_registrations';

export class UserRegistrationService {
  // Add a new user registration
  static async addUserRegistration(userData: UserRegistration): Promise<void> {
    try {
      const existingRegistrations = await this.getAllRegistrations();
      const newRegistration = {
        ...userData,
        registrationDate: new Date().toISOString(),
        isActive: true
      };
      
      existingRegistrations.push(newRegistration);
      await ProductionStorage.setItem(REGISTRATIONS_STORAGE_KEY, JSON.stringify(existingRegistrations));
    } catch (error) {
      console.error('Error adding user registration:', error);
      throw error;
    }
  }

  // Get all user registrations
  static async getAllRegistrations(): Promise<UserRegistration[]> {
    try {
      const registrationsData = await ProductionStorage.getItem(REGISTRATIONS_STORAGE_KEY);
      return registrationsData ? JSON.parse(registrationsData) : [];
    } catch (error) {
      console.error('Error getting user registrations:', error);
      return [];
    }
  }

  // Get registration statistics
  static async getRegistrationStats(): Promise<{
    totalUsers: number;
    totalCustomers: number;
    totalBusinessOwners: number;
    activeUsers: number;
    registrationsToday: number;
    registrationsThisWeek: number;
    registrationsThisMonth: number;
    recentRegistrations: UserRegistration[];
  }> {
    try {
      const registrations = await this.getAllRegistrations();
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

      const totalUsers = registrations.length;
      const totalCustomers = registrations.filter(r => r.userType === 'customer').length;
      const totalBusinessOwners = registrations.filter(r => r.userType === 'business').length;
      const activeUsers = registrations.filter(r => r.isActive).length;
      
      const registrationsToday = registrations.filter(r => 
        new Date(r.registrationDate) >= today
      ).length;
      
      const registrationsThisWeek = registrations.filter(r => 
        new Date(r.registrationDate) >= weekAgo
      ).length;
      
      const registrationsThisMonth = registrations.filter(r => 
        new Date(r.registrationDate) >= monthAgo
      ).length;

      const recentRegistrations = registrations
        .sort((a, b) => new Date(b.registrationDate).getTime() - new Date(a.registrationDate).getTime())
        .slice(0, 10);

      return {
        totalUsers,
        totalCustomers,
        totalBusinessOwners,
        activeUsers,
        registrationsToday,
        registrationsThisWeek,
        registrationsThisMonth,
        recentRegistrations
      };
    } catch (error) {
      console.error('Error getting registration stats:', error);
      return {
        totalUsers: 0,
        totalCustomers: 0,
        totalBusinessOwners: 0,
        activeUsers: 0,
        registrationsToday: 0,
        registrationsThisWeek: 0,
        registrationsThisMonth: 0,
        recentRegistrations: []
      };
    }
  }

  // Update user last login
  static async updateLastLogin(userId: string): Promise<void> {
    try {
      const registrations = await this.getAllRegistrations();
      const userIndex = registrations.findIndex(r => r.id === userId);
      
      if (userIndex !== -1) {
        registrations[userIndex].lastLogin = new Date().toISOString();
        await ProductionStorage.setItem(REGISTRATIONS_STORAGE_KEY, JSON.stringify(registrations));
      }
    } catch (error) {
      console.error('Error updating last login:', error);
    }
  }

  // Deactivate user
  static async deactivateUser(userId: string): Promise<void> {
    try {
      const registrations = await this.getAllRegistrations();
      const userIndex = registrations.findIndex(r => r.id === userId);
      
      if (userIndex !== -1) {
        registrations[userIndex].isActive = false;
        await ProductionStorage.setItem(REGISTRATIONS_STORAGE_KEY, JSON.stringify(registrations));
      }
    } catch (error) {
      console.error('Error deactivating user:', error);
    }
  }

  // Clear all registrations (for testing)
  static async clearAllRegistrations(): Promise<void> {
    try {
      await ProductionStorage.setItem(REGISTRATIONS_STORAGE_KEY, JSON.stringify([]));
    } catch (error) {
      console.error('Error clearing registrations:', error);
    }
  }
}

